/** Атрибут якоря онбординга в разметке */
export const ONBOARDING_ATTR = 'data-onboarding'

const POLL_INTERVAL_MS = 100

function isVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0)
    return false

  const style = window.getComputedStyle(element)

  return style.visibility !== 'hidden' && style.display !== 'none'
}

/**
 * Первый видимый элемент с якорем внутри корня.
 * Один якорь может стоять на нескольких элементах (десктопная и мобильная версии) — берём видимый.
 */
export function findAnchor(id: string, root: ParentNode = document): HTMLElement | null {
  const candidates = root.querySelectorAll<HTMLElement>(`[${ONBOARDING_ATTR}="${CSS.escape(id)}"]`)

  return Array.from(candidates).find(isVisible) ?? null
}

/**
 * Ждёт, пока все якоря появятся и станут видимыми.
 * Видимость проверяется опросом: она меняется и анимациями, которые мутаций не дают.
 * Если DOM не меняется дольше `settleMs`, а якоря так и нет — элемента на экране не будет
 * (нет прав, другое состояние), и ожидание заканчивается раньше общего таймаута.
 */
export function waitForAnchors(ids: string[], getRoot: () => ParentNode | null, timeoutMs: number, settleMs: number): Promise<HTMLElement[] | null> {
  return new Promise(resolve => {
    const startedAt = Date.now()
    let lastMutationAt = startedAt
    const observer = new MutationObserver(() => { lastMutationAt = Date.now() })

    observer.observe(document.body, { childList: true, subtree: true, attributes: true })

    const finish = (result: HTMLElement[] | null) => {
      observer.disconnect()
      resolve(result)
    }

    const check = () => {
      const root = getRoot()
      const elements = root ? ids.map(id => findAnchor(id, root)) : []

      if (root && elements.every(Boolean)) {
        finish(elements as HTMLElement[])

        return
      }

      const now = Date.now()
      if (now - startedAt >= timeoutMs || now - lastMutationAt >= settleMs) {
        finish(null)

        return
      }

      window.setTimeout(check, POLL_INTERVAL_MS)
    }

    check()
  })
}

/** Общий прямоугольник группы элементов в координатах окна; размонтированные и схлопнутые элементы не учитываются */
export function unionRect(elements: HTMLElement[]): DOMRect | null {
  const rects = elements
    .filter(element => element.isConnected)
    .map(element => element.getBoundingClientRect())
    .filter(rect => rect.width > 0 && rect.height > 0)

  if (!rects.length)
    return null

  const left = Math.min(...rects.map(rect => rect.left))
  const top = Math.min(...rects.map(rect => rect.top))
  const right = Math.max(...rects.map(rect => rect.right))
  const bottom = Math.max(...rects.map(rect => rect.bottom))

  return new DOMRect(left, top, right - left, bottom - top)
}

/** Плавно прокручивает первый элемент группы в центр, если группа не видна целиком */
export async function scrollIntoViewIfNeeded(elements: HTMLElement[]): Promise<void> {
  const rect = unionRect(elements)
  if (!rect)
    return

  const isInside = rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth
  if (isInside)
    return

  elements[0].scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
  await new Promise(resolve => window.setTimeout(resolve, 400))
}
